import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";

/** The fewest firms one send addresses. One firm is an ordinary proposal. */
export const MINIMUM_FIRMS_PER_SEND = 2;

/** The most firms one send addresses: `SendProposalToFirmsCommand.MaximumFirmCount` on the server. */
export const MAXIMUM_FIRMS_PER_SEND = 10;

/**
 * One firm a client sends a proposal to, to bid on the work (#576). The firm is addressed through
 * its representative, who leads the firm's team on the proposal it receives.
 */
export type ProposalFirm = {
  leader: EntityMember;
  /** The name the dialog shows for the firm. */
  name: string;
  members?: EntityMember[];
  marketplaceId?: Guid;
};

/** Whether a firm on the list can receive the proposal, and why not when it cannot. */
export type ProposalFirmReadiness = {
  firm: ProposalFirm;
  canReceive: boolean;
  reason?: string;
};

/**
 * What the review step can tell before anything is sent: a firm that is the client's own
 * organization, or that is already on the list, cannot receive the proposal. The server checks
 * both again, and reports what only it can know (a representative who cannot sign, a proposal its
 * rules do not let it send) per firm when the proposal is sent.
 */
export function reviewProposalFirms(
  firms: readonly ProposalFirm[],
  clientLegalEntityId: Guid | undefined,
): ProposalFirmReadiness[] {
  const seen = new Set<string>();
  return firms.map((firm) => {
    const entity = firm.leader.legalEntityId.value;
    if (clientLegalEntityId && firm.leader.legalEntityId.isEqualTo(clientLegalEntityId)) {
      return { firm, canReceive: false, reason: "This person works for your own organization." };
    }
    if (seen.has(entity)) {
      return { firm, canReceive: false, reason: "This firm is already on the list." };
    }
    seen.add(entity);
    return { firm, canReceive: true };
  });
}

/** Whether the list has a number of firms one send may address. */
export function isSendableFirmCount(count: number): boolean {
  return count >= MINIMUM_FIRMS_PER_SEND && count <= MAXIMUM_FIRMS_PER_SEND;
}
